import { useQuery, useQueryClient } from "@tanstack/react-query";
import Router from "next/router";
import { $isOrderUnpaid } from "~served/utils/calculation";
import { bigMath } from "~served/utils/misc";
import { gqlReq } from "~/libs/gql";
import {
	GetOperationalLocationsDocument,
	GetOperationalLocationsQuery,
} from "~/types/__generated/gql/graphql";
import { queryClient } from "~/providers/query-provider";

void (
	/* GraphQL */ `
		query GetOperationalLocations {
			getOperationalLocations {
				_id
				created_at
				updated_at
				venue
				name
				type
				payment_types
				status
				hash
				no_vat
				no_service_charge
				is_pay_later_allowed
				locations_groups_tags
				room_number
				default_order_note
				hidden_items_configs {
					items
					item_types
					categories
					is_items_hide_on_pos
					is_items_hide_on_qr
					is_categories_hide_on_pos
					is_categories_hide_on_qr
					is_item_types_hide_on_pos
					is_item_types_hide_on_qr
				}
				current_headcount
				_current_orders {
					_id
					is_paid
					is_cancelled
					is_bill_printed
					grand_total
				}
			}
		}
	`
);

export const getOperationalLocationsQueryKey = () => [
	"getOperationalLocations",
];

export const getOperationalLocationsOfflineQueryKey = () => [
	"getOperationalLocations",
	"offline",
];

export const getOperationalLocationsQueryFn = () =>
	gqlReq({
		document: GetOperationalLocationsDocument,
	});

export const useGetOperationalLocations = () => {
	const queryClient = useQueryClient();

	return useQuery({
		queryKey: getOperationalLocationsQueryKey(),
		queryFn: getOperationalLocationsQueryFn,
		onSuccess: (data) => {
			queryClient.setQueryData(getOperationalLocationsOfflineQueryKey(), data);
		},
	});
};

export const useGetOperationalLocationsOffline = () => {
	const queryClient = useQueryClient();

	return useQuery({
		queryKey: getOperationalLocationsOfflineQueryKey(),
		queryFn: () =>
			queryClient.getQueryData<GqlResultOf<GetOperationalLocationsQuery>>(
				getOperationalLocationsOfflineQueryKey(),
			) ?? [],
	});
};

export const getOperationalLocationsCache = () => {
	if (Router.pathname.startsWith("/offline/ops")) {
		const offlineCached = queryClient.getQueryData<
			GqlResultOf<GetOperationalLocationsQuery>
		>(getOperationalLocationsOfflineQueryKey());
		return offlineCached ?? [];
	}

	const cached = queryClient.getQueryData<
		GqlResultOf<GetOperationalLocationsQuery>
	>(getOperationalLocationsQueryKey());
	return cached ?? [];
};

export const getLocationUnpaidGrandTotal = (
	location: ArrayItem<GqlResultOf<GetOperationalLocationsQuery>>,
) =>
	location._current_orders
		// @ts-ignore
		.filter($isOrderUnpaid)
		.reduce((pre, cur) => bigMath.add(pre, cur.grand_total), 0);
